// src/pages/hr/HROffboarding.jsx
// ============================================================
// Production-Ready Offboarding & Exit Management Module
// Hierarchy: Employee/Lead/HR/Admin -> HR Review -> Admin Approval
// -> Notice Period -> Handover -> Asset Return -> Dept Clearances
// -> Admin Final Clearance -> Offboarded
// ============================================================

import React, { useState, useMemo } from "react";
import Layout from "../../components/common/Layout";
import { useAuth } from "../../context/AuthContext";
import { useAllUsers } from "../../hooks/useFirestore";
import { useMyAssets, returnAsset } from "../../hooks/useAssets";
import {
  useResignations,
  useMyResignation,
  useOffboardingHistory,
  submitResignation,
  hrApproveResignation,
  adminApproveResignation,
  rejectResignation,
  advanceToHandover,
  advanceToAssetReturn,
  startClearance,
  updateDepartmentClearance,
  adminFinalClearance,
} from "../../hooks/useOffboarding";

const STATUS_BADGES = {
  "Pending HR Review":         { bg: "#fef3c7", color: "#92400e", label: "Pending HR Review" },
  "Pending Admin Approval":   { bg: "#fed7aa", color: "#9a3412", label: "Pending Admin Approval" },
  "Notice Period Active":     { bg: "#dbeafe", color: "#1e40af", label: "Notice Period Active" },
  "Handover In Progress":     { bg: "#e0e7ff", color: "#3730a3", label: "Handover In Progress" },
  "Asset Return Verification": { bg: "#fce7f3", color: "#9d174d", label: "Asset Verification" },
  "Clearance In Progress":    { bg: "#ddd6fe", color: "#5b21b6", label: "Clearance In Progress" },
  "Admin Final Clearance":    { bg: "#ccfbf1", color: "#115e59", label: "Admin Final Clearance" },
  "Offboarded":               { bg: "#d1fae5", color: "#065f46", label: "Offboarded" },
  "Rejected":                 { bg: "#fee2e2", color: "#991b1b", label: "Rejected" },
  "Cancelled":                { bg: "#f3f4f6", color: "#374151", label: "Cancelled" },
};

const CLEARANCE_DEPTS = [
  { key: "hr", label: "HR Policies Clearance", icon: "📄" },
  { key: "teamLead", label: "Team Lead Clearance", icon: "👨‍💼" },
  { key: "it", label: "IT & Assets Clearance", icon: "💻" },
  { key: "finance", label: "Finance & Payroll Clearance", icon: "💳" },
  { key: "admin", label: "Admin & Access Clearance", icon: "🛡️" },
];

const WORKFLOW_STEPS = [
  { id: "Pending HR Review", label: "1. HR Review" },
  { id: "Pending Admin Approval", label: "2. Admin Approval" },
  { id: "Notice Period Active", label: "3. Notice Period" },
  { id: "Handover In Progress", label: "4. Handover" },
  { id: "Asset Return Verification", label: "5. Asset Return" },
  { id: "Clearance In Progress", label: "6. Dept Clearances" },
  { id: "Admin Final Clearance", label: "7. Admin Clearance" },
  { id: "Offboarded", label: "8. Offboarded" },
];

function fmtDate(dStr) {
  if (!dStr) return "—";
  try {
    const d = new Date(dStr + (dStr.includes("T") ? "" : "T00:00:00"));
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch (e) {
    return dStr;
  }
}

export default function HROffboarding() {
  const { userProfile } = useAuth();
  const role = (userProfile?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const isHR = role === "hr" || isAdmin;
  const isLead = role === "lead";

  const { data: allResignations, loading } = useResignations();
  const { data: myResignations } = useMyResignation(userProfile?.uid);
  const { data: historyLogs } = useOffboardingHistory();
  const { data: allUsers } = useAllUsers();

  // Selected view items — Only 3 tabs: resignations, clearance, history
  const [activeTab, setActiveTab] = useState("resignations");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedResignationId, setSelectedResignationId] = useState(null);

  // Keep selected resignation synced with real-time data
  const selectedResignation = useMemo(() => {
    if (!selectedResignationId) return null;
    return allResignations.find((r) => r.id === selectedResignationId) || null;
  }, [allResignations, selectedResignationId]);

  // Modals
  const [showResignModal, setShowResignModal] = useState(false);
  const [showClearanceModal, setShowClearanceModal] = useState(null); // deptKey
  const [actionModal, setActionModal] = useState(null); // { type, title, resignation }
  const [modalComments, setModalComments] = useState("");
  const [modalLWD, setModalLWD] = useState("");
  const [saving, setSaving] = useState(false);

  // Forms
  const [resignForm, setResignForm] = useState({
    resignationDate: new Date().toISOString().split("T")[0],
    proposedLWD: "",
    noticePeriodDays: 30,
    reason: "",
    handoverNotes: "",
  });

  const [clearanceForm, setClearanceForm] = useState({
    status: "Approved",
    comments: "",
  });

  // Resignations list based on role
  const displayResignations = useMemo(() => {
    let list = isHR ? allResignations : isLead ? allResignations.filter((r) => r.teamLeadId === userProfile?.uid || r.department === userProfile?.department) : myResignations;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.employeeName?.toLowerCase().includes(q) ||
          r.employeeId?.toLowerCase().includes(q) ||
          r.department?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") {
      list = list.filter((r) => r.status === statusFilter);
    }
    return list;
  }, [allResignations, myResignations, isHR, isLead, userProfile, search, statusFilter]);

  // Assets of selected resignation employee
  const { data: userAssets } = useMyAssets(selectedResignation?.userUid);

  // KPI Metrics
  const kpis = useMemo(() => {
    return {
      total: allResignations.length,
      noticeActive: allResignations.filter((r) => r.status === "Notice Period Active").length,
      pendingClearance: allResignations.filter((r) => 
        r.status === "Clearance In Progress" || 
        r.status === "Pending HR Review" || 
        r.status === "Pending Admin Approval" ||
        r.status === "Handover In Progress" ||
        r.status === "Asset Return Verification" ||
        r.status === "Admin Final Clearance"
      ).length,
      completed: allResignations.filter((r) => r.status === "Offboarded").length,
    };
  }, [allResignations]);

  // ── Actions ─────────────────────────────────────────────

  async function handleResignSubmit(e) {
    e.preventDefault();
    if (!resignForm.proposedLWD || !resignForm.reason) {
      alert("Please specify Last Working Day and Reason.");
      return;
    }
    setSaving(true);
    try {
      await submitResignation(resignForm, userProfile);
      alert("Resignation submitted successfully. Pending HR Review.");
      setShowResignModal(false);
    } catch (err) {
      alert(err.message || "Failed to submit resignation.");
    }
    setSaving(false);
  }

  async function executeWorkflowAction() {
    if (!actionModal) return;
    setSaving(true);
    try {
      const { type, resignation } = actionModal;
      if (type === "hrApprove") {
        await hrApproveResignation(resignation.id, modalComments, userProfile);
        alert("Resignation reviewed by HR and passed to Admin for approval.");
      } else if (type === "adminApprove") {
        await adminApproveResignation(resignation.id, modalLWD || resignation.proposedLWD, modalComments, userProfile);
        alert("Resignation approved by Admin! Notice period is now active.");
      } else if (type === "reject") {
        await rejectResignation(resignation.id, modalComments, userProfile);
        alert("Resignation request rejected.");
      } else if (type === "advanceHandover") {
        await advanceToHandover(resignation.id, userProfile);
        alert("Moved to Task & Knowledge Handover.");
      } else if (type === "advanceAsset") {
        await advanceToAssetReturn(resignation.id, userProfile);
        alert("Moved to Asset Return Verification.");
      } else if (type === "startClearance") {
        await startClearance(resignation.id, userProfile);
        alert("Department Clearances initiated.");
      } else if (type === "adminFinalClearance") {
        await adminFinalClearance(resignation.id, modalComments, userProfile);
        alert("Admin Final Clearance granted! Employee is now marked OFFBOARDED.");
      }
      setActionModal(null);
      setModalComments("");
      setModalLWD("");
    } catch (err) {
      alert(err.message || "Workflow action failed.");
    }
    setSaving(false);
  }

  async function handleClearanceSave() {
    if (!selectedResignation || !showClearanceModal) return;
    setSaving(true);
    try {
      await updateDepartmentClearance(
        selectedResignation.id,
        showClearanceModal,
        clearanceForm,
        userProfile
      );
      setShowClearanceModal(null);
    } catch (err) {
      alert(err.message || "Clearance update failed.");
    }
    setSaving(false);
  }

  async function handleReturnAsset(assetId) {
    if (!window.confirm("Mark this asset as returned to inventory?")) return;
    try {
      await returnAsset(assetId, userProfile, `Returned during offboarding of ${selectedResignation?.employeeName}`);
      alert("Asset marked as returned!");
    } catch (err) {
      alert(err.message || "Failed to return asset.");
    }
  }

  // ── Render UI ───────────────────────────────────────────

  return (
    <Layout>
      <style>{`
        .offboard-kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        .offboard-kpi {
          background: var(--bg-card, #fff);
          border: 1px solid var(--border, #e5e7eb);
          border-radius: 12px;
          padding: 1rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.85rem;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .offboard-kpi__icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          flex-shrink: 0;
        }
        .offboard-kpi__val {
          font-size: 1.5rem;
          font-weight: 800;
          line-height: 1;
          color: var(--text-primary);
        }
        .offboard-kpi__label {
          font-size: 0.72rem;
          color: var(--text-muted);
          margin-top: 2px;
          text-transform: uppercase;
        }
        .offboard-tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid var(--border, #e5e7eb);
          margin-bottom: 1.25rem;
          overflow-x: auto;
        }
        .offboard-tab {
          padding: 0.65rem 1.1rem;
          border: none;
          background: none;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-muted);
          cursor: pointer;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          white-space: nowrap;
        }
        .offboard-tab.active {
          color: var(--primary, #1b4b31);
          border-bottom-color: var(--primary, #1b4b31);
        }
        .clearance-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        .clearance-card {
          background: var(--bg-card, #fff);
          border: 1px solid var(--border, #e5e7eb);
          border-radius: 12px;
          padding: 1rem 1.25rem;
        }

        /* Workflow Stepper */
        .stepper-container {
          display: flex;
          gap: 0.5rem;
          overflow-x: auto;
          padding: 0.75rem 0.5rem;
          background: var(--bg-secondary, #f8fafc);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 12px;
          margin-bottom: 1.25rem;
        }
        .stepper-step {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.75rem;
          font-weight: 700;
          padding: 0.4rem 0.75rem;
          border-radius: 20px;
          white-space: nowrap;
          background: #e2e8f0;
          color: #64748b;
        }
        .stepper-step.active {
          background: #1b4b31;
          color: #ffffff;
          box-shadow: 0 2px 6px rgba(27, 75, 49, 0.25);
        }
        .stepper-step.passed {
          background: #d1fae5;
          color: #065f46;
        }
      `}</style>

      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">🚪 Offboarding & Exit Management</h1>
          <p className="page-subtitle">
            EMS Hierarchy: HR Review → Admin Approval → Notice Period → Handover → Asset Return → Clearances → Admin Final Clearance → Offboarded
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button className="btn-primary" onClick={() => setShowResignModal(true)}>
            + Submit Resignation
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="offboard-kpi-grid">
        <div className="offboard-kpi">
          <div className="offboard-kpi__icon" style={{ background: "#ede9fe", color: "#5b21b6" }}>📄</div>
          <div>
            <div className="offboard-kpi__val">{kpis.total}</div>
            <div className="offboard-kpi__label">Resignations</div>
          </div>
        </div>
        <div className="offboard-kpi">
          <div className="offboard-kpi__icon" style={{ background: "#dbeafe", color: "#1e40af" }}>⏳</div>
          <div>
            <div className="offboard-kpi__val">{kpis.noticeActive}</div>
            <div className="offboard-kpi__label">Notice Active</div>
          </div>
        </div>
        <div className="offboard-kpi">
          <div className="offboard-kpi__icon" style={{ background: "#fef3c7", color: "#92400e" }}>📋</div>
          <div>
            <div className="offboard-kpi__val">{kpis.pendingClearance}</div>
            <div className="offboard-kpi__label">Pending / In Progress</div>
          </div>
        </div>
        <div className="offboard-kpi">
          <div className="offboard-kpi__icon" style={{ background: "#d1fae5", color: "#065f46" }}>✅</div>
          <div>
            <div className="offboard-kpi__val">{kpis.completed}</div>
            <div className="offboard-kpi__label">Offboarded</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs — Only 3 tabs */}
      <div className="offboard-tabs">
        {[
          { key: "resignations", label: "Resignations & Notice Period" },
          { key: "clearance", label: "Exit Clearances & Assets" },
          { key: "history", label: "Audit History" },
        ].map((t) => (
          <button
            key={t.key}
            className={`offboard-tab ${activeTab === t.key ? "active" : ""}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: RESIGNATIONS & NOTICE PERIOD ──────────────── */}
      {activeTab === "resignations" && (
        <div>
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
            <input
              type="text"
              className="form-input"
              placeholder="🔍 Search employee, ID, or department…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 220 }}
            />
            <select
              className="form-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: 220 }}
            >
              <option value="all">All Statuses</option>
              {Object.keys(STATUS_BADGES).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Resignation Date</th>
                  <th>Notice Days</th>
                  <th>Proposed / Official LWD</th>
                  <th>Status</th>
                  <th>Hierarchy Action Bar</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: "2rem" }}>Loading resignations…</td></tr>
                ) : displayResignations.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: "2rem" }}>No resignation records found.</td></tr>
                ) : (
                  displayResignations.map((r) => {
                    const badge = STATUS_BADGES[r.status] || STATUS_BADGES["Pending HR Review"];
                    const isSelected = selectedResignationId === r.id;

                    return (
                      <tr key={r.id} style={{ background: isSelected ? "rgba(27,75,49,0.06)" : "transparent" }}>
                        <td>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>{r.employeeName}</div>
                          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{r.employeeId || "—"}</div>
                        </td>
                        <td>{r.department}</td>
                        <td>{fmtDate(r.resignationDate)}</td>
                        <td>{r.noticePeriodDays || 30} days</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{fmtDate(r.officialLWD || r.proposedLWD)}</div>
                          {r.officialLWD && r.officialLWD !== r.proposedLWD && (
                            <div style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>Proposed: {fmtDate(r.proposedLWD)}</div>
                          )}
                        </td>
                        <td>
                          <span style={{ padding: "3px 10px", borderRadius: 99, fontSize: "0.7rem", fontWeight: 700, background: badge.bg, color: badge.color, whiteSpace: "nowrap" }}>
                            {badge.label}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                            <button
                              className="btn-secondary-sm"
                              onClick={() => {
                                setSelectedResignationId(r.id);
                                setActiveTab("clearance");
                              }}
                            >
                              Manage Exit
                            </button>

                            {/* 1. HR Review Step -> HR / Admin */}
                            {r.status === "Pending HR Review" && isHR && (
                              <button
                                className="btn-primary-sm"
                                style={{ background: "#d97706", borderColor: "#d97706" }}
                                onClick={() => setActionModal({ type: "hrApprove", title: "HR Review & Pass to Admin", resignation: r })}
                              >
                                HR Verify & Pass to Admin
                              </button>
                            )}

                            {/* 2. Admin Final Approval Step -> Admin ONLY */}
                            {r.status === "Pending Admin Approval" && isAdmin && (
                              <button
                                className="btn-primary-sm"
                                style={{ background: "#1b4b31" }}
                                onClick={() => {
                                  setModalLWD(r.proposedLWD);
                                  setActionModal({ type: "adminApprove", title: "Admin Final Approval (Start Notice)", resignation: r });
                                }}
                              >
                                Admin Approve Notice
                              </button>
                            )}

                            {/* 3. Notice Active -> Handover */}
                            {r.status === "Notice Period Active" && isHR && (
                              <button
                                className="btn-secondary-sm"
                                onClick={() => setActionModal({ type: "advanceHandover", title: "Advance to Handover Phase", resignation: r })}
                              >
                                Start Handover
                              </button>
                            )}

                            {/* 4. Handover -> Asset Return */}
                            {r.status === "Handover In Progress" && isHR && (
                              <button
                                className="btn-secondary-sm"
                                onClick={() => setActionModal({ type: "advanceAsset", title: "Advance to Asset Return Phase", resignation: r })}
                              >
                                Verify Assets
                              </button>
                            )}

                            {/* 5. Asset Return -> Clearances */}
                            {r.status === "Asset Return Verification" && isHR && (
                              <button
                                className="btn-secondary-sm"
                                onClick={() => setActionModal({ type: "startClearance", title: "Start Dept Clearances", resignation: r })}
                              >
                                Start Clearances
                              </button>
                            )}

                            {/* 6. Admin Final Clearance -> Admin ONLY */}
                            {r.status === "Admin Final Clearance" && isAdmin && (
                              <button
                                className="btn-primary-sm"
                                style={{ background: "#059669", borderColor: "#059669" }}
                                onClick={() => setActionModal({ type: "adminFinalClearance", title: "Admin Final Clearance (Complete Offboarding)", resignation: r })}
                              >
                                Mark Offboarded
                              </button>
                            )}

                            {/* Reject Option for HR/Admin on pending requests */}
                            {(r.status === "Pending HR Review" || r.status === "Pending Admin Approval") && isHR && (
                              <button
                                className="btn-secondary-sm"
                                style={{ color: "#dc2626", borderColor: "#fca5a5" }}
                                onClick={() => setActionModal({ type: "reject", title: "Reject Resignation", resignation: r })}
                              >
                                Reject
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TAB 2: EXIT CLEARANCES & ASSETS ─────────────────── */}
      {activeTab === "clearance" && (
        <div>
          {selectedResignation ? (
            <div style={{ background: "var(--bg-card, #fff)", border: "1px solid var(--border, #e5e7eb)", borderRadius: 12, padding: "1.25rem", marginBottom: "1.5rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: "1.1rem" }}>
                    Exit Workflow Tracker: {selectedResignation.employeeName} ({selectedResignation.employeeId || "—"})
                  </h3>
                  <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Department: {selectedResignation.department} · LWD: {fmtDate(selectedResignation.officialLWD || selectedResignation.proposedLWD)} · Current Status: <b>{selectedResignation.status}</b>
                  </div>
                </div>
                <button className="btn-secondary-sm" onClick={() => setSelectedResignationId(null)}>Change Employee</button>
              </div>

              {/* Workflow Stepper */}
              <div className="stepper-container">
                {WORKFLOW_STEPS.map((step, idx) => {
                  const currentIdx = WORKFLOW_STEPS.findIndex((s) => s.id === selectedResignation.status);
                  const isPassed = currentIdx > idx || selectedResignation.status === "Offboarded";
                  const isActive = step.id === selectedResignation.status;

                  return (
                    <div
                      key={step.id}
                      className={`stepper-step ${isActive ? "active" : isPassed ? "passed" : ""}`}
                    >
                      {isPassed ? "✓ " : ""}{step.label}
                    </div>
                  );
                })}
              </div>

              {/* 5 Department Clearance Cards */}
              <h4 style={{ margin: "1rem 0 0.5rem 0", fontSize: "0.95rem" }}>🏢 Department Exit Clearances</h4>
              <div className="clearance-grid">
                {CLEARANCE_DEPTS.map((dept) => {
                  const c = selectedResignation.clearances?.[dept.key] || { status: "Pending" };
                  const isApp = c.status === "Approved";
                  const isRej = c.status === "Rejected";

                  return (
                    <div key={dept.key} className="clearance-card" style={{ borderLeft: `4px solid ${isApp ? "#10b981" : isRej ? "#ef4444" : "#f59e0b"}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontWeight: 700, fontSize: "0.88rem" }}>
                          {dept.icon} {dept.label}
                        </div>
                        <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: isApp ? "#d1fae5" : isRej ? "#fee2e2" : "#fef3c7", color: isApp ? "#065f46" : isRej ? "#991b1b" : "#92400e" }}>
                          {c.status}
                        </span>
                      </div>

                      {c.approvedBy && (
                        <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 8 }}>
                          Approved by: <b>{c.approvedBy}</b> on {fmtDate(c.approvedAt)}
                        </div>
                      )}
                      {c.comments && (
                        <div style={{ fontSize: "0.75rem", background: "var(--bg-secondary, #f9fafb)", padding: "6px 8px", borderRadius: 6, marginTop: 6 }}>
                          "{c.comments}"
                        </div>
                      )}

                      {/* Action buttons */}
                      {isHR && (
                        <div style={{ marginTop: 12, display: "flex", gap: "0.5rem" }}>
                          <button
                            className="btn-secondary-sm"
                            onClick={() => {
                              setShowClearanceModal(dept.key);
                              setClearanceForm({ status: "Approved", comments: "" });
                            }}
                          >
                            Update Status
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Asset Return Integration Section */}
              <div style={{ marginTop: "2rem", borderTop: "1px solid var(--border, #e5e7eb)", paddingTop: "1.25rem" }}>
                <h4 style={{ margin: "0 0 0.75rem 0", fontSize: "0.95rem" }}>💻 Assigned Hardware & Software Assets Return Checklist</h4>
                {!userAssets || userAssets.length === 0 ? (
                  <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>No assets currently assigned to this employee.</div>
                ) : (
                  <div className="data-table-wrap">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Asset Tag</th>
                          <th>Name / Category</th>
                          <th>Serial No</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userAssets.map((asset) => (
                          <tr key={asset.id}>
                            <td style={{ fontWeight: 700 }}>{asset.assetTag}</td>
                            <td>{asset.name} ({asset.category})</td>
                            <td>{asset.serialNumber || "—"}</td>
                            <td><span style={{ padding: "2px 8px", borderRadius: 99, fontSize: "0.7rem", fontWeight: 700, background: "#dbeafe", color: "#1e40af" }}>Assigned</span></td>
                            <td>
                              <button className="btn-primary-sm" onClick={() => handleReturnAsset(asset.id)}>
                                ↩ Mark Returned
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "3rem", background: "var(--bg-card, #fff)", borderRadius: 12, border: "1px solid var(--border, #e5e7eb)" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>📋</div>
              <h3>Select a Resignation to Manage Exit Clearance</h3>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Go to Resignations tab and click "Manage Exit" on an employee record.</p>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 3: AUDIT HISTORY ────────────────────────────── */}
      {activeTab === "history" && (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Employee</th>
                <th>Action</th>
                <th>Performed By</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {historyLogs.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: "2rem" }}>No audit log history found.</td></tr>
              ) : (
                historyLogs.map((h) => (
                  <tr key={h.id}>
                    <td style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      {h.timestamp?.toDate ? h.timestamp.toDate().toLocaleString("en-IN") : "—"}
                    </td>
                    <td style={{ fontWeight: 600 }}>{h.employeeName}</td>
                    <td><span style={{ fontWeight: 700, fontSize: "0.8rem", color: "#1b4b31" }}>{h.action}</span></td>
                    <td>{h.performedByName} ({h.performedByRole})</td>
                    <td style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{h.details}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── RESIGNATION SUBMIT MODAL ────────────────────────── */}
      {showResignModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--bg-card, #fff)", borderRadius: 16, width: "min(520px, 95vw)", padding: "1.5rem", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <h3 style={{ margin: "0 0 1rem 0" }}>Submit Resignation</h3>
            <form onSubmit={handleResignSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label className="form-label">Resignation Date</label>
                <input type="date" className="form-input" value={resignForm.resignationDate} onChange={(e) => setResignForm({ ...resignForm, resignationDate: e.target.value })} required />
              </div>
              <div>
                <label className="form-label">Proposed Last Working Day (LWD)</label>
                <input type="date" className="form-input" value={resignForm.proposedLWD} onChange={(e) => setResignForm({ ...resignForm, proposedLWD: e.target.value })} required />
              </div>
              <div>
                <label className="form-label">Notice Period (Days)</label>
                <input type="number" className="form-input" value={resignForm.noticePeriodDays} onChange={(e) => setResignForm({ ...resignForm, noticePeriodDays: e.target.value })} required />
              </div>
              <div>
                <label className="form-label">Reason for Resignation</label>
                <textarea className="form-input" rows={3} value={resignForm.reason} onChange={(e) => setResignForm({ ...resignForm, reason: e.target.value })} placeholder="State primary reason..." required />
              </div>
              <div>
                <label className="form-label">Handover Notes</label>
                <textarea className="form-input" rows={2} value={resignForm.handoverNotes} onChange={(e) => setResignForm({ ...resignForm, handoverNotes: e.target.value })} placeholder="Details of project handover..." />
              </div>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: 10 }}>
                <button type="button" className="btn-secondary" onClick={() => setShowResignModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? "Submitting…" : "Submit Resignation"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── WORKFLOW ACTION MODAL ───────────────────────────── */}
      {actionModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--bg-card, #fff)", borderRadius: 16, width: "min(460px, 95vw)", padding: "1.5rem", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <h3 style={{ margin: "0 0 0.5rem 0" }}>{actionModal.title}</h3>
            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", margin: "0 0 1rem 0" }}>
              Employee: <b>{actionModal.resignation.employeeName}</b> ({actionModal.resignation.department})
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              {actionModal.type === "adminApprove" && (
                <div>
                  <label className="form-label">Official Last Working Day (LWD)</label>
                  <input
                    type="date"
                    className="form-input"
                    value={modalLWD}
                    onChange={(e) => setModalLWD(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label className="form-label">Review / Approval Comments</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={modalComments}
                  onChange={(e) => setModalComments(e.target.value)}
                  placeholder="Enter comments or reason..."
                />
              </div>

              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: 10 }}>
                <button className="btn-secondary" onClick={() => setActionModal(null)}>Cancel</button>
                <button className="btn-primary" onClick={executeWorkflowAction} disabled={saving}>
                  {saving ? "Processing…" : "Confirm & Proceed"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CLEARANCE MODAL ─────────────────────────────────── */}
      {showClearanceModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--bg-card, #fff)", borderRadius: 16, width: "min(440px, 95vw)", padding: "1.5rem", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <h3 style={{ margin: "0 0 1rem 0" }}>Update Clearance: {showClearanceModal.toUpperCase()}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div>
                <label className="form-label">Clearance Status</label>
                <select className="form-input" value={clearanceForm.status} onChange={(e) => setClearanceForm({ ...clearanceForm, status: e.target.value })}>
                  <option value="Approved">Approved</option>
                  <option value="Pending">Pending</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="form-label">Review Comments</label>
                <textarea className="form-input" rows={3} value={clearanceForm.comments} onChange={(e) => setClearanceForm({ ...clearanceForm, comments: e.target.value })} placeholder="Optional comments..." />
              </div>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: 10 }}>
                <button className="btn-secondary" onClick={() => setShowClearanceModal(null)}>Cancel</button>
                <button className="btn-primary" onClick={handleClearanceSave} disabled={saving}>{saving ? "Saving…" : "Save Clearance"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
